import styles from "../../styles/addReceipt.module.css";
import { formatCurrency } from "../../../../../shared/utils/formatCurrency";
import { parseMoney, sanitizeMoneyTyping } from "../../../../../shared/utils/parseMoney";
import { useEffect, useState } from "react";
import { showAlertFavor } from "./Alerts";

/**
 * InvoiceCxc
 *
 * El campo de abono se muestra y acepta valores en la moneda ORIGINAL
 * de la factura (USD para facturas USD, CRC para facturas CRC).
 *
 * Para USD: acepta hasta 4 decimales para evitar residuos de redondeo
 * cuando el monto CRC / TC_compra no cae exacto en 2 decimales.
 * Ej: ₡51,000 / 508 = $100.3937 → el usuario puede ingresar $100.3937
 * y el restante queda en ₡0.00 en vez de ₡1.88.
 *
 * Props:
 *   inv            — CxC item (con .moneda, .saldo, .abono en moneda original)
 *   updateAbono    — (id, value) → value en moneda original de la factura
 *   updateChecked  — (id, bool) → checkbox toggle
 *   remainingCRC   — presupuesto restante en CRC (para validación interna en useCxc)
 *   TC_compra      — tipo de cambio de compra (para calcular límites)
 */

/** Formatea un valor USD mostrando hasta 4 decimales, pero nunca menos de 2 */
function fmtUSD(n) {
  const s = Number(n).toFixed(4);
  // Elimina ceros finales pero mantiene mínimo 2 decimales
  return s.replace(/(\.\d{2})0+$/, "$1");
}

export function InvoiceCxc({
  inv,
  updateAbono,
  updateChecked,
  toggleDiferencial,
  toggleComisionBancaria,
  remainingCRC,
  TC_compra   = 1,
  bancoMoneda = "CRC",
  metodoPago  = "TRANSFERENCIA",
}) {
  const [displayValue, setDisplayValue] = useState("");
  const [isEditing, setIsEditing]       = useState(false);

  const esUSD  = inv.moneda === "USD";
  const tc     = Number(TC_compra) || 1;

  // Calcular máximo permitido en moneda de la factura.
  // Para USD no se capea al saldo — el exceso genera saldo a favor.
  const saldoInv = Number(inv.saldo);
  const maxEnMonedaInv = esUSD
    ? (remainingCRC || 0) / tc + (inv.abono || 0)
    : Math.min(saldoInv, (remainingCRC || 0) + (inv.abono || 0));

  useEffect(() => {
    if (!isEditing) {
      if (inv.abono === 0) {
        setDisplayValue("");
      } else if (esUSD) {
        setDisplayValue(fmtUSD(inv.abono));
      } else {
        setDisplayValue(formatCurrency(inv.abono));
      }
    }
  }, [inv.abono, isEditing, esUSD]);

  return (
    <tr>
      <td>
        <input
          type="checkbox"
          checked={inv.checked}
          onChange={(e) => updateChecked(inv.id, e.target.checked)}
        />
      </td>
      <td>{inv.factura?.numero_factura ?? "—"}</td>
      <td>{inv.factura?.cuenta_ingreso?.nombre ?? "—"}</td>
      <td className={styles.saldo}>
        {esUSD ? (
          <>
            <span style={{ color: "#60a5fa" }}>${Number(inv.saldo).toFixed(2)}</span>
            {inv.saldo_crc != null && (
              <span style={{ display: "block", fontSize: "0.7rem", color: "var(--text-muted)", marginTop: 1 }}>
                ≈ ₡{formatCurrency(inv.saldo_crc)}
              </span>
            )}
          </>
        ) : (
          <>₡{formatCurrency(inv.saldo)}</>
        )}
      </td>
      <td>
        {/* Prefijo de moneda */}
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <span style={{
            fontSize: "0.78rem", color: esUSD ? "#60a5fa" : "var(--text-muted)", fontWeight: 600,
          }}>
            {esUSD ? "$" : "₡"}
          </span>
          <input
            type="text"
            className={styles["input-abono"]}
            value={displayValue}
            disabled={!inv.checked}
            placeholder={esUSD ? "0.0000" : "0.00"}
            onFocus={() => {
              setIsEditing(true);
              if (inv.abono) {
                setDisplayValue(
                  esUSD
                    ? fmtUSD(inv.abono)
                    : inv.abono.toString()
                );
              } else {
                setDisplayValue("");
              }
            }}
            onChange={(e) => {
              // Saldo a favor (negativo) — caso especial
              if (inv.negative === true || inv.saldo < 0) {
                const raw = sanitizeMoneyTyping(e.target.value);
                if (raw === "" || raw === "-") { setDisplayValue(raw); return; }
                let num = parseMoney(raw);
                const min = Number(inv.saldo);
                num = Math.min(0, Math.max(min, num));
                setDisplayValue(num.toString());
                updateAbono(inv.id, num);
                return;
              }

              // Caso normal — en moneda de la factura (parseo tolerante: punto o coma decimal)
              const raw = sanitizeMoneyTyping(e.target.value);
              const num = parseMoney(raw);
              if (!esUSD && num > maxEnMonedaInv + 0.01) {
                const alert = showAlertFavor(num - maxEnMonedaInv);
                alert.fire();
              }
              setDisplayValue(raw);
              updateAbono(inv.id, num);
            }}
            onBlur={() => {
              setIsEditing(false);
              if (inv.abono === 0) {
                setDisplayValue("");
              } else if (esUSD) {
                setDisplayValue(fmtUSD(inv.abono));
              } else {
                setDisplayValue(formatCurrency(inv.abono));
              }
            }}
          />
        </div>
        {Number(inv.comision_bancaria) > 0 && (
          <div style={{ fontSize: "0.68rem", color: "#fcd34d", marginTop: 2 }}>
            <i className="fa-solid fa-credit-card" style={{ marginRight: 3 }} />
            + comisión {esUSD ? "$" : "₡"}
            {esUSD ? fmtUSD(inv.comision_bancaria) : formatCurrency(inv.comision_bancaria)}
          </div>
        )}
      </td>

      {/* Botón "tratar exceso como Comisión Bancaria" (pagos con Tarjeta) o
          "tratar exceso como Diferencial Cambiario" (banco y factura de monedas distintas). */}
      <td style={{ paddingLeft: 6, verticalAlign: "middle" }}>
        {inv.checked && metodoPago === "TARJETA" && toggleComisionBancaria ? (
          <button
            type="button"
            disabled={
              !(Number(inv.comision_bancaria) > 0) &&
              (saldoInv - Number(inv.abono || 0)) <= 0.005
            }
            title={
              Number(inv.comision_bancaria) > 0
                ? "Diferencia tratada como Comisión Bancaria — click para revertir"
                : "Tratar diferencia como Comisión Bancaria (pago con Tarjeta)"
            }
            onClick={() => toggleComisionBancaria(inv.id)}
            style={{
              padding: "2px 8px",
              fontSize: "0.72rem",
              fontWeight: 600,
              borderRadius: 4,
              cursor: "pointer",
              border: Number(inv.comision_bancaria) > 0
                ? "1px solid rgba(250,204,21,0.6)"
                : "1px solid rgba(148,163,184,0.3)",
              background: Number(inv.comision_bancaria) > 0
                ? "rgba(250,204,21,0.12)"
                : "transparent",
              color: Number(inv.comision_bancaria) > 0 ? "#fcd34d" : "var(--text-muted)",
              whiteSpace: "nowrap",
              transition: "all 0.15s",
            }}
          >
            {Number(inv.comision_bancaria) > 0 ? (
              <><i className="fa-solid fa-rotate-left" style={{ marginRight: 4 }} />Comisión ✓</>
            ) : (
              <><i className="fa-solid fa-credit-card" style={{ marginRight: 4 }} />→ Comisión</>
            )}
          </button>
        ) : inv.checked && bancoMoneda !== inv.moneda && toggleDiferencial ? (
          <button
            type="button"
            title={
              inv.tratar_como_diferencial
                ? "Exceso tratado como Diferencial Cambiario — click para revertir"
                : "Tratar exceso como Diferencial Cambiario en vez de Saldo a Favor"
            }
            onClick={() => toggleDiferencial(inv.id)}
            style={{
              padding: "2px 8px",
              fontSize: "0.72rem",
              fontWeight: 600,
              borderRadius: 4,
              cursor: "pointer",
              border: inv.tratar_como_diferencial
                ? "1px solid rgba(250,204,21,0.6)"
                : "1px solid rgba(148,163,184,0.3)",
              background: inv.tratar_como_diferencial
                ? "rgba(250,204,21,0.12)"
                : "transparent",
              color: inv.tratar_como_diferencial ? "#fcd34d" : "var(--text-muted)",
              whiteSpace: "nowrap",
              transition: "all 0.15s",
            }}
          >
            {inv.tratar_como_diferencial ? (
              <><i className="fa-solid fa-rotate-left" style={{ marginRight: 4 }} />Diferencial ✓</>
            ) : (
              <><i className="fa-solid fa-arrows-rotate" style={{ marginRight: 4 }} />→ Diferencial</>
            )}
          </button>
        ) : (
          <span style={{ display: "inline-block", width: 2 }} />
        )}
      </td>
    </tr>
  );
}
